import * as THREE from 'three';

/** @typedef {{ min: THREE.Vector3; max: THREE.Vector3; isFloor?: boolean }} BoxCollider */

const _playerMin = new THREE.Vector3();
const _playerMax = new THREE.Vector3();
const _staticBox = new THREE.Box3();
const _playerBox = new THREE.Box3();
const _centerA = new THREE.Vector3();
const _centerB = new THREE.Vector3();

/**
 * Capsule-style player (cylinder AABB) vs static AABB colliders.
 */
export class PlayerCollision {
  /**
   * @param {{
   *   radius: number;
   *   height: number;
   *   groundSnapDistance?: number;
   * }} config
   */
  constructor({ radius, height, groundSnapDistance = 0.14 }) {
    this.radius = radius;
    this.height = height;
    this.groundSnapDistance = groundSnapDistance;
  }

  /**
   * @param {THREE.Vector3} position feet
   * @param {THREE.Vector3} velocity
   * @param {BoxCollider[]} colliders
   * @param {number} deltaSeconds
   * @returns {{ onGround: boolean }}
   */
  move(position, velocity, colliders, deltaSeconds) {
    let onGround = false;

    position.x += velocity.x * deltaSeconds;
    for (let i = 0; i < 4; i += 1) {
      if (!this._resolveHorizontal(position, colliders)) break;
    }

    position.z += velocity.z * deltaSeconds;
    for (let i = 0; i < 4; i += 1) {
      if (!this._resolveHorizontal(position, colliders)) break;
    }

    position.y += velocity.y * deltaSeconds;
    for (const box of colliders) {
      if (this._resolveVertical(position, velocity, box)) {
        onGround = true;
      }
    }

    if (!onGround) {
      onGround = this._snapToGround(position, velocity, colliders);
    }

    return { onGround };
  }

  /**
   * @param {THREE.Vector3} feet
   * @param {BoxCollider[]} colliders
   */
  findGroundHeight(feet, colliders) {
    let floor = 0;
    for (const box of colliders) {
      if (!this._overlapsXZ(feet, box)) continue;
      floor = Math.max(floor, box.max.y);
    }
    return floor;
  }

  _resolveHorizontal(position, colliders) {
    let moved = false;
    this._setPlayerAabb(position);

    for (const box of colliders) {
      if (box.isFloor) continue;

      _staticBox.min.copy(box.min);
      _staticBox.max.copy(box.max);
      if (!_playerBox.intersectsBox(_staticBox)) continue;

      const overlapX = Math.min(
        _playerBox.max.x - _staticBox.min.x,
        _staticBox.max.x - _playerBox.min.x,
      );
      const overlapZ = Math.min(
        _playerBox.max.z - _staticBox.min.z,
        _staticBox.max.z - _playerBox.min.z,
      );

      if (overlapX <= 0 || overlapZ <= 0) continue;

      _playerBox.getCenter(_centerA);
      _staticBox.getCenter(_centerB);

      if (overlapX < overlapZ) {
        position.x += _centerA.x < _centerB.x ? -overlapX : overlapX;
      } else {
        position.z += _centerA.z < _centerB.z ? -overlapZ : overlapZ;
      }

      this._setPlayerAabb(position);
      moved = true;
    }

    return moved;
  }

  _resolveVertical(position, velocity, box) {
    this._setPlayerAabb(position);
    _staticBox.min.copy(box.min);
    _staticBox.max.copy(box.max);
    if (!_playerBox.intersectsBox(_staticBox)) return false;

    const overlapDown = _staticBox.max.y - _playerBox.min.y;
    if (overlapDown >= 0 && overlapDown < 0.6 && velocity.y <= 0) {
      position.y += overlapDown;
      velocity.y = 0;
      return true;
    }

    const overlapUp = _playerBox.max.y - _staticBox.min.y;
    if (overlapUp >= 0 && overlapUp < 0.6 && velocity.y > 0) {
      position.y -= overlapUp;
      velocity.y = 0;
    }

    return false;
  }

  _snapToGround(position, velocity, colliders) {
    if (velocity.y > 0.5) return false;

    let bestFloor = -Infinity;
    for (const box of colliders) {
      if (!this._overlapsXZ(position, box)) continue;
      if (position.y <= box.max.y + 0.08 && position.y >= box.max.y - 0.35) {
        bestFloor = Math.max(bestFloor, box.max.y);
      }
    }

    if (bestFloor > -Infinity) {
      position.y = bestFloor;
      velocity.y = 0;
      return true;
    }
    return false;
  }

  _setPlayerAabb(feet) {
    _playerMin.set(feet.x - this.radius, feet.y, feet.z - this.radius);
    _playerMax.set(
      feet.x + this.radius,
      feet.y + this.height,
      feet.z + this.radius,
    );
    _playerBox.min.copy(_playerMin);
    _playerBox.max.copy(_playerMax);
  }

  _overlapsXZ(feet, box) {
    const closestX = clamp(feet.x, box.min.x, box.max.x);
    const closestZ = clamp(feet.z, box.min.z, box.max.z);
    const dx = feet.x - closestX;
    const dz = feet.z - closestZ;
    return dx * dx + dz * dz < this.radius * this.radius;
  }
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}
